import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, MapPin, Users, Heart, Eye, Share2, CreditCard, Info } from 'lucide-react';
import { domain } from './Domain.jsx';

const EventCard = ({
    event,
    showShareButtons = true,
    showLikeButton = true,
    showViewButton = true,
    className = "",
    onLike,
    onShare,
    price = null,
    currency = "Ugx"
}) => {
    const [showShareButtonsState, setShowShareButtonsState] = useState(false);
    const [isLiked, setIsLiked] = useState(false);
    const [showAllPrices, setShowAllPrices] = useState(false);
    const navigate = useNavigate();

    const toggleShareButtons = () => {
        setShowShareButtonsState(!showShareButtonsState);
        if (onShare) onShare(event);
    };

    const handleImageDoubleClick = () => {
        navigate(`/events/${event.slug}`);
    };

    const handleLike = () => {
        setIsLiked(!isLiked);
        if (onLike) onLike(event, !isLiked);
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    // Truncate text
    const truncateText = (text, maxLength = 100) => {
        if (!text) return '';
        return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    };

    // Get currency to display (from prop, event object, or default)
    // Shorten currency name if it's too long (e.g., "Ugandan Shillings (Ugx)" -> "Ugx")
    const getCurrencyDisplay = (currencyName) => {
        if (!currencyName) return "Ugx";
        // If currency contains "Ugx" or "UGX", use that
        if (currencyName.toLowerCase().includes('ugx')) {
            return "Ugx";
        }
        // If currency is very long, try to extract abbreviation
        if (currencyName.length > 10) {
            const match = currencyName.match(/\(([A-Z]{2,4})\)/);
            if (match) return match[1];
        }
        return currencyName;
    };

    const displayCurrency = currency !== "Ugx" ? getCurrencyDisplay(currency) : getCurrencyDisplay(event.currency) || "Ugx";

    // Format price
    const formatPrice = (priceValue) => {
        if (priceValue === null || priceValue === undefined) return null;
        if (priceValue === 0) return 'Free';
        return `${displayCurrency} ${parseFloat(priceValue).toLocaleString()}`;
    };

    // Get price to display (from prop or event object)
    const displayPrice = price !== null ? price : (event.min_price || event.cheapest_ticket_price || null);
    const displayMaxPrice = event.max_price || null;
    const ticketPrices = event.ticket_prices || [];
    const hasMultiplePrices = ticketPrices.length > 1;
    const hasSinglePrice = ticketPrices.length === 1;

    // Format price range display
    const formatPriceRange = () => {
        if (displayPrice === null || displayPrice === undefined) return null;
        if (displayPrice === 0) return 'Free';

        // If there's a price range and they're different
        if (displayMaxPrice && displayMaxPrice !== displayPrice && displayMaxPrice > displayPrice) {
            return `${formatPrice(displayPrice)} - ${formatPrice(displayMaxPrice)}`;
        }

        // Single price or no range
        return formatPrice(displayPrice);
    };

    // Get first ticket for single price display
    const firstTicket = ticketPrices.length > 0 ? ticketPrices[0] : null;

    return (
        <motion.div
            className={`bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group cursor-pointer border border-gray-100 ${className}`}
            whileHover={{ y: -5 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
        >
            {/* Image Section */}
            <div className="relative overflow-hidden">
                <img
                    src={domain + event.banner}
                    className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
                    alt={event.event_plot_name}
                    style={{
                        height: '256px',
                        objectFit: 'cover',
                        width: '100%',
                        display: 'block'
                    }}
                    onError={(e) => {
                        e.target.src = '/images/default-event.jpg';
                    }}
                />
                {/* Category Badge */}
                <div className="absolute top-3 left-3">
                    <span className="bg-gradient-to-r from-orange-400 to-orange-500 text-white px-3 py-1 rounded-full text-xs font-medium shadow-lg">
                        {event.event_category?.name}
                    </span>
                </div>
                {/* Details Icon */}
                <div className="absolute z-1 top-4 right-3">
                    <Link to={`/events/${event.slug}`}>
                        <div className="bg-white bg-opacity-90 backdrop-blur-sm rounded-full p-2 shadow-lg group-hover:bg-opacity-100 transition-all duration-300 hover:scale-110 cursor-pointer">
                            <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                    </Link>
                </div>
            </div>

            {/* Content Section */}
            <div className="p-6">
                {/* Title */}
                <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-orange-500 transition-colors duration-200">
                    {event.event_plot_name}
                </h3>

                {/* Description */}
                <p onClick={handleImageDoubleClick} className="text-gray-600 text-sm mb-4 line-clamp-2 leading-relaxed">
                    {truncateText(event.event_description)}
                </p>

                {/* Event Details */}
                <div className="space-y-2 mb-4">
                    <div className="flex items-center text-sm text-gray-700">
                        <Calendar className="w-4 h-4 text-orange-500 mr-2 flex-shrink-0" />
                        <span className="font-medium">{formatDate(event.start_date_event)}</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                        <MapPin className="w-4 h-4 text-teal-500 mr-2 flex-shrink-0" />
                        <span className="truncate">{event.venue_name}</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                        <Users className="w-4 h-4 text-blue-500 mr-2 flex-shrink-0" />
                        <span className="truncate">{event.organiser?.organiser_name}</span>
                    </div>
                </div>

                {/* Stats Bar */}
                <div className="relative py-2 border-t border-gray-100">
                    {/* Single Price Badge on Image */}
                    {hasSinglePrice && firstTicket && displayPrice !== null && (
                        <div>
                            <div className="px-0 py-1 rounded-lg">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2 text-orange-500!">
                                        <CreditCard className="w-6 h-6" />
                                        <div className='flex gap-2'>
                                            <div className="font-bold text-sm!">
                                                {getCurrencyDisplay(firstTicket.currency || displayCurrency)} {parseFloat(firstTicket.ticket_price).toLocaleString()}
                                            </div>
                                            <div className="text-sm! font-medium opacity-90">
                                                {firstTicket.ticket_category?.name} {firstTicket.ticket_type && `(${firstTicket.ticket_type.name})`}
                                            </div>
                                        </div>
                                    </div>
                                    {showShareButtons && (
                                        <div className="relative">
                                            <button
                                                onClick={toggleShareButtons}
                                                className="text-gray-500! flex items-center hover:text-teal-500! transition-colors duration-200 p-0! m-0! outline-0!"
                                            >
                                                <Share2 className="w-4 h-4" />
                                            </button>

                                            {/* Share Buttons - Displayed Vertically Upwards */}
                                            <AnimatePresence>
                                                {showShareButtonsState && (
                                                    <motion.div
                                                        initial={{ opacity: 0, y: 10, scale: 0.8 }}
                                                        animate={{ opacity: 1, y: 0, scale: 1 }}
                                                        exit={{ opacity: 0, y: 10, scale: 0.8 }}
                                                        transition={{ duration: 0.2 }}
                                                        className="absolute bottom-full right-0 mb-2 flex flex-col items-center space-y-2 z-50"
                                                    >
                                                        <button
                                                            className="w-8 h-8 bg-green-500 hover:bg-green-600 rounded-full flex items-center justify-center text-white transition-colors duration-200 shadow-lg"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                if (onShare) onShare(event);
                                                            }}
                                                        >
                                                            <i className="fa-brands fa-whatsapp text-sm"></i>
                                                        </button>
                                                        <button
                                                            className="w-8 h-8 bg-blue-500 hover:bg-blue-600 rounded-full flex items-center justify-center text-white transition-colors duration-200 shadow-lg"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                if (onShare) onShare(event);
                                                            }}
                                                        >
                                                            <i className="fa-brands fa-facebook text-sm"></i>
                                                        </button>
                                                        <button
                                                            className="w-8 h-8 bg-sky-500 hover:bg-sky-600 rounded-full flex items-center justify-center text-white transition-colors duration-200 shadow-lg"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                if (onShare) onShare(event);
                                                            }}
                                                        >
                                                            <i className="fa-brands fa-twitter text-sm"></i>
                                                        </button>
                                                        <button
                                                            className="w-8 h-8 bg-blue-700 hover:bg-blue-800 rounded-full flex items-center justify-center text-white transition-colors duration-200 shadow-lg"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                if (onShare) onShare(event);
                                                            }}
                                                        >
                                                            <i className="fa-brands fa-linkedin text-sm"></i>
                                                        </button>
                                                    </motion.div>
                                                )}
                                            </AnimatePresence>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Price Range Badge on Image for Multiple Prices */}
                    {hasMultiplePrices && displayPrice !== null && (
                        <div className="flex items-center justify-between gap-2">
                            <div className="py-1.5 rounded-lg text-orange-500! flex items-center gap-1.5">
                                <CreditCard className="w-6 h-6" />
                                <span className="font-bold  text-sm!">{formatPriceRange()}</span>
                            </div>
                            <div className='flex items-center'>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setShowAllPrices(true);
                                    }}
                                    className="bg-white! flex items-center text-gray-500! hover:text-teal-500! font-sm! text-xs! py-1.5 outline-0!"
                                >
                                    <Info className='w-4 h-4' /> View All
                                </button>
                                {showShareButtons && (
                                    <div className="relative">
                                        <button
                                            onClick={toggleShareButtons}
                                            className="text-gray-500! flex items-center hover:text-teal-500! transition-colors duration-200 p-0! m-0! outline-0!"
                                        >
                                            <Share2 className="w-4 h-4" />
                                        </button>

                                        {/* Share Buttons - Displayed Vertically Upwards */}
                                        <AnimatePresence>
                                            {showShareButtonsState && (
                                                <motion.div
                                                    initial={{ opacity: 0, y: 10, scale: 0.8 }}
                                                    animate={{ opacity: 1, y: 0, scale: 1 }}
                                                    exit={{ opacity: 0, y: 10, scale: 0.8 }}
                                                    transition={{ duration: 0.2 }}
                                                    className="absolute bottom-full right-0 mb-2 flex flex-col items-center space-y-2 z-50"
                                                >
                                                    <button
                                                        className="w-8 h-8 bg-green-500 hover:bg-green-600 rounded-full flex items-center justify-center text-white transition-colors duration-200 shadow-lg"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (onShare) onShare(event);
                                                        }}
                                                    >
                                                        <i className="fa-brands fa-whatsapp text-sm"></i>
                                                    </button>
                                                    <button
                                                        className="w-8 h-8 bg-blue-500 hover:bg-blue-600 rounded-full flex items-center justify-center text-white transition-colors duration-200 shadow-lg"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (onShare) onShare(event);
                                                        }}
                                                    >
                                                        <i className="fa-brands fa-facebook text-sm"></i>
                                                    </button>
                                                    <button
                                                        className="w-8 h-8 bg-sky-500 hover:bg-sky-600 rounded-full flex items-center justify-center text-white transition-colors duration-200 shadow-lg"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (onShare) onShare(event);
                                                        }}
                                                    >
                                                        <i className="fa-brands fa-twitter text-sm"></i>
                                                    </button>
                                                    <button
                                                        className="w-8 h-8 bg-blue-700 hover:bg-blue-800 rounded-full flex items-center justify-center text-white transition-colors duration-200 shadow-lg"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (onShare) onShare(event);
                                                        }}
                                                    >
                                                        <i className="fa-brands fa-linkedin text-sm"></i>
                                                    </button>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Expanded Price List Overlay on Image */}
                    {hasMultiplePrices && showAllPrices && (
                        <AnimatePresence>
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.2 }}
                                className="absolute bottom-12 z-999 right-0 left-0 bg-black/60 backdrop-blur-sm rounded-t-xl"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShowAllPrices(false);
                                }}
                            >
                                <div
                                    className="w-full bg-white rounded-t-xl shadow-2xl max-h-[70%] overflow-y-auto"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <div className="sticky top-0 bg-gradient-to-r from-orange-500 to-orange-600 text-white! px-4! py-3! rounded-t-xl flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <CreditCard className="w-4 h-4" />
                                            <span className="font-bold text-sm!">All Ticket Prices</span>
                                        </div>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setShowAllPrices(false);
                                            }}
                                            className="text-xs! font-medium bg-white/20! hover:bg-white/30! px-2 py-1 rounded transition-colors"
                                        >
                                            Hide
                                        </button>
                                    </div>
                                    <div className="p-4 space-y-2">
                                        {ticketPrices.map((ticket, index) => (
                                            <div
                                                key={ticket.id || index}
                                                className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2 hover:bg-gray-100 transition-colors"
                                            >
                                                <div className="flex items-center gap-2 flex-1 min-w-0 text-sm!">
                                                    {ticket.ticket_category && (
                                                        <span className="text-gray-800 font-semibold text-sm!">
                                                            {ticket.ticket_category.name}
                                                        </span>
                                                    )}
                                                    {ticket.ticket_type && (
                                                        <span className="text-gray-500 text-xs!">
                                                            ({ticket.ticket_type.name})
                                                        </span>
                                                    )}
                                                </div>
                                                <span className="text-orange-600 font-bold text-sm ml-3 flex-shrink-0">
                                                    {getCurrencyDisplay(ticket.currency || displayCurrency)} {parseFloat(ticket.ticket_price).toLocaleString()}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </motion.div>
                        </AnimatePresence>
                    )}
                </div>

                {/* Old Stats Bar */}
                <div className="items-center justify-between py-3 border-t border-gray-100 hidden">
                    <div className="flex items-center space-x-4">
                        {showLikeButton && (
                            <button
                                onClick={handleLike}
                                className={`flex items-center space-x-1 text-sm transition-colors duration-200 ${isLiked ? 'text-red-500' : 'text-gray-500 hover:text-red-500'
                                    }`}
                            >
                                <Heart className={`w-4 h-4 ${isLiked ? 'fill-current' : ''}`} />
                                <span>{event.likes || 0}</span>
                            </button>
                        )}
                        {showViewButton && (
                            <div className="flex items-center space-x-1 text-sm text-gray-500">
                                <Eye className="w-4 h-4" />
                                <span>{event.views || 0}</span>
                            </div>
                        )}
                    </div>
                    {showShareButtons && (
                        <div className="relative">
                            <button
                                onClick={toggleShareButtons}
                                className="text-gray-500 hover:text-teal-500 transition-colors duration-200"
                            >
                                <Share2 className="w-4 h-4" />
                            </button>

                            {/* Share Buttons - Displayed Vertically Upwards */}
                            <AnimatePresence>
                                {showShareButtonsState && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 10, scale: 0.8 }}
                                        animate={{ opacity: 1, y: 0, scale: 1 }}
                                        exit={{ opacity: 0, y: 10, scale: 0.8 }}
                                        transition={{ duration: 0.2 }}
                                        className="absolute bottom-full right-0 mb-2 flex flex-col items-center space-y-2 z-50"
                                    >
                                        <button
                                            className="w-8 h-8 bg-green-500 hover:bg-green-600 rounded-full flex items-center justify-center text-white transition-colors duration-200 shadow-lg"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (onShare) onShare(event);
                                            }}
                                        >
                                            <i className="fa-brands fa-whatsapp text-sm"></i>
                                        </button>
                                        <button
                                            className="w-8 h-8 bg-blue-500 hover:bg-blue-600 rounded-full flex items-center justify-center text-white transition-colors duration-200 shadow-lg"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (onShare) onShare(event);
                                            }}
                                        >
                                            <i className="fa-brands fa-facebook text-sm"></i>
                                        </button>
                                        <button
                                            className="w-8 h-8 bg-sky-500 hover:bg-sky-600 rounded-full flex items-center justify-center text-white transition-colors duration-200 shadow-lg"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (onShare) onShare(event);
                                            }}
                                        >
                                            <i className="fa-brands fa-twitter text-sm"></i>
                                        </button>
                                        <button
                                            className="w-8 h-8 bg-blue-700 hover:bg-blue-800 rounded-full flex items-center justify-center text-white transition-colors duration-200 shadow-lg"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (onShare) onShare(event);
                                            }}
                                        >
                                            <i className="fa-brands fa-linkedin text-sm"></i>
                                        </button>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    )}
                </div>
            </div>
        </motion.div>
    );
};

export default EventCard; 